/**
 * Get Relevant Excel Files Tool
 * 
 * Discovers Excel files from multiple sources to help the user/LLM
 * choose which file to edit. This tool is called BEFORE instantiating
 * the LinkedExcelEditorAgent.
 * 
 * ## Data Sources
 * 
 * 1. **Open Excel Instances** (Python: COM)
 *    - Files currently open in Excel
 *    - Highest priority (most relevant)
 *    
 * 2. **Recent Files** (Python: Windows Recent Items)
 *    - Files recently accessed on the system
 *    - Sorted by last accessed time
 *    
 * 3. **Presentation References** (TypeScript: Live Tracker) [FUTURE]
 *    - Files already linked to the current PowerPoint
 *    - Read from custom XML tags
 * 
 * ## Usage Flow
 * 
 * ```
 * 1. getRelevantExcelFiles()     → Discover files
 * 2. UserInterviewTool           → User selects file  
 * 3. LinkedExcelEditorAgent      → Edit the selected file
 * ```
 */

import { ToolBase } from "../../../base/toolBase";
import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { 
    GetRelevantExcelFilesParamsSchema, 
    type GetRelevantExcelFilesParams, 
    type GetRelevantExcelFilesResponse,
    type ExcelFileInfo,
} from "./getRelevantExcelFiles.schema";
import { EXCEL_BACKEND_BASE_URL } from "../../../../../services/communication/backendClient";

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

// Excel operations go to the Excel microservice on port 8001
const API_BASE_URL = EXCEL_BACKEND_BASE_URL;

// ═══════════════════════════════════════════════════════════════════════════
// TOOL DESCRIPTION
// ═══════════════════════════════════════════════════════════════════════════

const GET_RELEVANT_EXCEL_FILES_DESCRIPTION = `Discover Excel files relevant to the current task.

## Purpose

Call this tool BEFORE editing an Excel file to find available options.
Returns a prioritized list of Excel files from multiple sources.

## Sources (in priority order)

1. **Currently Open in Excel** - Files the user has open right now
2. **Referenced in PowerPoint** - Files already linked to charts/tables in the presentation
3. **Recently Used** - Files recently accessed on the system

## When to Use

- User mentions editing an Excel file but doesn't specify which one
- User wants to update data in a linked chart/table
- You need to select from multiple possible Excel sources

## Output

Returns files with:
- File path and name
- Whether currently open in Excel
- Source (how it was discovered)
- Workbook ID (if known)
- PowerPoint references (if from presentation)

## Next Steps

After getting the file list:
1. If only one relevant file → proceed with LinkedExcelEditorAgent
2. If multiple files → use UserInterviewTool to let user select
3. If user specified a path → skip this tool, go directly to LinkedExcelEditorAgent`;

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const GET_EXCEL_FILES_SYSTEM_PROMPT = `### getRelevantExcelFiles
Discover Excel files available for editing.

**When to use:**
- User mentions editing Excel but doesn't specify which file
- Before calling linkedExcelEditorAgent when file is unknown
- To see what Excel files are currently open

**Sources (priority order):**
1. Files currently open in Excel
2. Files referenced in the PowerPoint presentation
3. Recently used files

**Workflow:**
1. Call getRelevantExcelFiles() → get list of options
2. If multiple files → use userInterview to let user choose
3. Then call linkedExcelEditorAgent with selected file

\`\`\`json
{
  "includeOpenFiles": true,
  "includeRecentFiles": true,
  "maxRecentFiles": 10
}
\`\`\``;

// ═══════════════════════════════════════════════════════════════════════════
// GET RELEVANT EXCEL FILES TOOL
// ═══════════════════════════════════════════════════════════════════════════

export class GetRelevantExcelFilesTool extends ToolBase<GetRelevantExcelFilesParams, GetRelevantExcelFilesResponse> {
    public readonly name = "getRelevantExcelFiles";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = GET_EXCEL_FILES_SYSTEM_PROMPT;
    
    private apiBaseUrl: string;
    
    // Streaming field configuration (simple tool, minimal streaming)
    fieldsToCheck = [
        { name: "uiDescription", type: "string" as const, list_status: false, order: 0 },
        { name: "maxRecentFiles", type: "number" as const, list_status: false, order: 1 },
    ];

    constructor(apiBaseUrl: string = API_BASE_URL) {
        super();
        this.apiBaseUrl = apiBaseUrl;
        this.description = new ToolDescription(
            "getRelevantExcelFiles",
            GET_RELEVANT_EXCEL_FILES_DESCRIPTION,
            GetRelevantExcelFilesParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<GetRelevantExcelFilesResponse> {
        // Validate parameters
        const parseResult = safeParseToolParams(GetRelevantExcelFilesParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(parseResult.error);
        }
        
        const params = parseResult.data;
        
        console.log(`[GetRelevantExcelFilesTool] Discovering Excel files...`);
        
        const allFiles: ExcelFileInfo[] = [];
        let openCount = 0;
        let recentCount = 0;
        let presentationRefCount = 0;
        
        try {
            // 1. Get open files from Python backend
            if (params.includeOpen) {
                const openFiles = await this.fetchOpenFiles();
                openCount = openFiles.length;
                allFiles.push(...openFiles);
                console.log(`[GetRelevantExcelFilesTool] Found ${openCount} open files`);
            }
            
            // 2. Get presentation references from Live Tracker (future)
            if (params.includePresentationRefs) {
                const presentationRefs = await this.fetchPresentationReferences();
                
                // Add refs that aren't already in the list (dedup by path)
                const existingPaths = new Set(allFiles.map(f => f.path.toLowerCase()));
                for (const ref of presentationRefs) {
                    if (!existingPaths.has(ref.path.toLowerCase())) {
                        // Check if it's also open
                        ref.isOpen = allFiles.some(
                            f => f.path.toLowerCase() === ref.path.toLowerCase() && f.isOpen
                        );
                        allFiles.push(ref);
                        existingPaths.add(ref.path.toLowerCase());
                        presentationRefCount++;
                    } else {
                        // Merge references into existing file entry
                        const existing = allFiles.find(
                            f => f.path.toLowerCase() === ref.path.toLowerCase()
                        );
                        if (existing && ref.references.length > 0) {
                            existing.references.push(...ref.references);
                            if (existing.source !== "open_instance") {
                                existing.source = "presentation_reference";
                            }
                        }
                    }
                }
                console.log(`[GetRelevantExcelFilesTool] Found ${presentationRefCount} presentation references`);
            }
            
            // 3. Get recent files from Python backend
            if (params.includeRecent) {
                const recentFiles = await this.fetchRecentFiles(params.maxRecentFiles);
                
                // Add files that aren't already in the list (dedup by path)
                const existingPaths = new Set(allFiles.map(f => f.path.toLowerCase()));
                for (const file of recentFiles) {
                    if (!existingPaths.has(file.path.toLowerCase())) {
                        allFiles.push(file);
                        existingPaths.add(file.path.toLowerCase());
                        recentCount++;
                    }
                }
                console.log(`[GetRelevantExcelFilesTool] Added ${recentCount} recent files`);
            }
            
            // Generate summary for LLM
            const summary = this.generateSummary(allFiles, openCount, presentationRefCount, recentCount);
            
            console.log(`[GetRelevantExcelFilesTool] Total: ${allFiles.length} files`);
            
            return {
                files: allFiles,
                count: allFiles.length,
                sources: {
                    openCount,
                    recentCount,
                    presentationRefCount,
                },
                summary,
            };
            
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error(`[GetRelevantExcelFilesTool] Error:`, error);
            throw new Error(`Failed to discover Excel files: ${errorMsg}`);
        }
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // DATA FETCHING
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Fetch currently open Excel files from Python backend
     */
    private async fetchOpenFiles(): Promise<ExcelFileInfo[]> {
        try {
            const response = await fetch(`${this.apiBaseUrl}/excel/open-files`);
            
            if (!response.ok) {
                console.warn(`[GetRelevantExcelFilesTool] Failed to fetch open files: ${response.status}`);
                return [];
            }
            
            const data = await response.json();
            return (data.files || []).map((f: any) => this.mapBackendFile(f, "open_instance"));
            
        } catch (error) {
            console.warn(`[GetRelevantExcelFilesTool] Error fetching open files:`, error);
            return [];
        }
    }
    
    /**
     * Fetch recent Excel files from Python backend
     */
    private async fetchRecentFiles(maxFiles: number): Promise<ExcelFileInfo[]> {
        try {
            const response = await fetch(
                `${this.apiBaseUrl}/excel/recent-files?max_files=${maxFiles}`
            );
            
            if (!response.ok) {
                console.warn(`[GetRelevantExcelFilesTool] Failed to fetch recent files: ${response.status}`);
                return [];
            }
            
            const data = await response.json();
            return (data.files || []).map((f: any) => this.mapBackendFile(f, "recent"));
            
        } catch (error) {
            console.warn(`[GetRelevantExcelFilesTool] Error fetching recent files:`, error);
            return [];
        }
    }
    
    /**
     * Fetch presentation references from Live Tracker
     * 
     * TODO: Implement when Live Tracker stores Excel references.
     * For now, returns empty array.
     */
    private async fetchPresentationReferences(): Promise<ExcelFileInfo[]> {
        // TODO: Read from Live Tracker / Deck Registry
        // const documentId = await getOrCreatedocumentId();
        // const refs = LiveTracker.getExcelReferences(documentId);
        // return refs.map(ref => this.mapPresentationRef(ref));
        
        // Placeholder until Live Tracker integration
        return [];
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // HELPERS
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Map backend file response to our interface
     */
    private mapBackendFile(
        file: any, 
        defaultSource: "open_instance" | "recent" | "presentation_reference"
    ): ExcelFileInfo {
        return {
            path: file.path || "",
            fileName: file.fileName || file.file_name || this.extractFileName(file.path),
            workbookId: file.workbookId || file.workbook_id || null,
            isOpen: file.isOpen ?? file.is_open ?? (defaultSource === "open_instance"),
            source: file.source || defaultSource,
            lastAccessed: file.lastAccessed || file.last_accessed || null,
            lastModified: file.lastModified || file.last_modified || null,
            fileSizeKb: file.fileSizeKb || file.file_size_kb || null,
            references: file.references || [],
        };
    }
    
    /**
     * Extract file name from full path
     */
    private extractFileName(path: string): string {
        if (!path) return "Unknown";
        const parts = path.replace(/\\/g, "/").split("/");
        return parts[parts.length - 1] || "Unknown";
    }
    
    /**
     * Generate a human-readable summary for the LLM
     */
    private generateSummary(
        files: ExcelFileInfo[],
        _openCount: number,
        _presentationRefCount: number,
        _recentCount: number
    ): string {
        if (files.length === 0) {
            return "No Excel files found. The user may need to open a file in Excel or provide a specific path.";
        }
        
        const lines: string[] = [];
        lines.push(`Found ${files.length} Excel file(s):`);
        
        // Group by source
        const openFiles = files.filter(f => f.source === "open_instance");
        const refFiles = files.filter(f => f.source === "presentation_reference");
        const recentFiles = files.filter(f => f.source === "recent");
        
        if (openFiles.length > 0) {
            lines.push("");
            lines.push(`Currently Open (${openFiles.length}):`);
            for (const f of openFiles.slice(0, 5)) {
                lines.push(`  • ${f.fileName}${f.workbookId ? ` [ID: ${f.workbookId}]` : ""}`);
            }
            if (openFiles.length > 5) {
                lines.push(`  ... and ${openFiles.length - 5} more`);
            }
        }
        
        if (refFiles.length > 0) {
            lines.push("");
            lines.push(`Referenced in Presentation (${refFiles.length}):`);
            for (const f of refFiles.slice(0, 5)) {
                const refCount = f.references.length;
                lines.push(`  • ${f.fileName} (${refCount} reference${refCount !== 1 ? "s" : ""})`);
            }
            if (refFiles.length > 5) {
                lines.push(`  ... and ${refFiles.length - 5} more`);
            }
        }
        
        if (recentFiles.length > 0) {
            lines.push("");
            lines.push(`Recently Used (${recentFiles.length}):`);
            for (const f of recentFiles.slice(0, 5)) {
                lines.push(`  • ${f.fileName}`);
            }
            if (recentFiles.length > 5) {
                lines.push(`  ... and ${recentFiles.length - 5} more`);
            }
        }
        
        return lines.join("\n");
    }
}

export default GetRelevantExcelFilesTool;
